/* Expense > Mileage — trip entry with a LIVE IRS-rate reimbursement calculator (Phase 1).
   The rate comes from the accounting-api (GET /v1/expense/mileage-rate) for the trip date, so
   the number shown is the authoritative one the server uses at posting — no drifting client
   constant. "Add to report" ensures the trip month's report exists (idempotent) then appends a
   MILEAGE line; the server recomputes miles × rate on save. */
import { type FormEvent, useMemo, useState } from 'react';
import { Button, Card } from '@andavo/web-ui';

import { useAddMileageLine, useMileageRate } from './hooks/use-mileage';
import { money } from './lib/format';
import { Field, Input } from './ui/field';

const TODAY = new Date().toISOString().slice(0, 10);

export function MileageScreen() {
  const [txnDate, setTxnDate] = useState(TODAY);
  const [miles, setMiles] = useState('');
  const [description, setDescription] = useState('');
  const [glAccountId, setGlAccountId] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: rate, isLoading: rateLoading } = useMileageRate(txnDate);
  const addMileage = useAddMileageLine();

  const reimbursement = useMemo(() => {
    const m = Number(miles);
    const r = rate ? Number(rate.ratePerMile) : NaN;
    if (Number.isNaN(m) || Number.isNaN(r) || m <= 0) return null;
    return m * r;
  }, [miles, rate]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (!miles || Number(miles) <= 0) return;
    addMileage.mutate(
      {
        txnDate,
        miles,
        description: description || undefined,
        glAccountId: glAccountId ? Number(glAccountId) : undefined,
      },
      {
        onSuccess: () => {
          setDone(true);
          setMiles('');
          setDescription('');
        },
        onError: (err) =>
          setError(
            err instanceof Error ? err.message : 'Could not add the trip.',
          ),
      },
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <Card className="p-6">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Trip date" htmlFor="m-date">
              <Input
                id="m-date"
                type="date"
                value={txnDate}
                onChange={(e) => setTxnDate(e.target.value)}
                required
              />
            </Field>
            <Field
              label="Miles"
              htmlFor="m-miles"
              hint="Round-trip business miles."
            >
              <Input
                id="m-miles"
                type="number"
                step="0.1"
                min="0"
                value={miles}
                onChange={(e) => setMiles(e.target.value)}
                required
              />
            </Field>
            <Field label="Purpose" htmlFor="m-desc" className="sm:col-span-2">
              <Input
                id="m-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Client site visit — Acme Corp"
              />
            </Field>
            <Field label="GL account" htmlFor="m-gl">
              <Input
                id="m-gl"
                type="number"
                value={glAccountId}
                onChange={(e) => setGlAccountId(e.target.value)}
                placeholder="e.g. 6200"
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-surface-midground p-4">
            <div className="flex flex-col gap-0.5">
              <span className="font-inter text-content-secondary text-xs uppercase tracking-wide">
                IRS rate ({txnDate.slice(0, 4)})
              </span>
              <span className="font-sans font-semibold text-content-primary text-lg">
                {rateLoading
                  ? '…'
                  : rate
                    ? `$${Number(rate.ratePerMile).toFixed(4)}/mi`
                    : '—'}
              </span>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className="font-inter text-content-secondary text-xs uppercase tracking-wide">
                Reimbursement
              </span>
              <span className="font-sans font-semibold text-content-action text-2xl">
                {reimbursement != null ? money(reimbursement) : '—'}
              </span>
            </div>
          </div>

          {error && (
            <p className="font-inter text-content-danger text-sm">{error}</p>
          )}
          {done && (
            <p className="font-inter text-content-success text-sm">
              Added to your {txnDate.slice(0, 7)} report.
            </p>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={addMileage.isPending || !miles}
            >
              Add to report
            </Button>
          </div>
        </form>
      </Card>

      <p className="font-inter text-content-secondary text-sm">
        The reimbursement is recomputed on the server from the IRS rate in
        effect on the trip date, so historical trips keep the correct rate
        across the annual January change.
      </p>
    </div>
  );
}
